import {
  InfiniteListBase,
  ShowBase,
  useGetList,
  useGetOne,
  useRecordContext,
  useRedirect,
  useTranslate,
} from "ra-core";
import { Pencil } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { DeleteButton } from "@/components/admin/delete-button";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { buttonVariants } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

import { contactFullName } from "../contacts/contactModel";
import { fullScreenOnMobile } from "../layout/mobileDialog";
import { NoteCreate } from "../notes/NoteCreate";
import { NotesIterator } from "../notes/NotesIterator";
import type { Company, Contact, Sponsoring } from "../types";
import { ContractTypeBadge } from "./ContractTypeName";
import { EndDateField } from "./EndDateStatus";
import { SaleName } from "./SaleName";
import { StageBadge } from "./StageBadge";

/** Show dialog, opened on `${basePath}/:id/show` on top of the list */
export const SponsoringShow = ({
  open,
  id,
  basePath = "/sponsorings",
}: {
  open: boolean;
  id?: string;
  /** The overview this dialog belongs to (/sponsorings, /balsponsoren, /leads) */
  basePath?: string;
}) => {
  const redirect = useRedirect();
  const translate = useTranslate();
  const handleClose = () => redirect(basePath);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        className={`lg:max-w-3xl p-6 overflow-y-auto max-h-9/10 top-1/20 translate-y-0 ${fullScreenOnMobile}`}
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">
          {translate("resources.sponsorings.name", { smart_count: 1 })}
        </DialogTitle>
        {id ? (
          <ShowBase id={id}>
            <SponsoringShowContent basePath={basePath} />
          </ShowBase>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Body of the show view: company, badges, details, the company block and the
 * notes. Shared by the desktop dialog and the mobile show page, which renders
 * its actions in the page header instead (`hideActions`).
 */
export const SponsoringShowContent = ({
  hideActions = false,
  basePath = "/sponsorings",
}: {
  hideActions?: boolean;
  /** The overview this view belongs to (/sponsorings, /balsponsoren, /leads) */
  basePath?: string;
} = {}) => {
  const translate = useTranslate();
  const record = useRecordContext<Sponsoring>();
  if (!record) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-start gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl font-semibold truncate">
            <ReferenceField
              source="company_id"
              reference="companies"
              link="show"
            >
              <TextField source="name" />
            </ReferenceField>
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ContractTypeBadge />
            <StageBadge stage={record.stage} />
          </div>
        </div>
        {hideActions ? null : (
          <div className="flex gap-2 pr-8 shrink-0">
            <Link
              className={buttonVariants({ variant: "outline" })}
              to={`${basePath}/${record.id}`}
            >
              <Pencil />
              {translate("resources.sponsorings.action.edit")}
            </Link>
            <DeleteButton redirect={basePath} />
          </div>
        )}
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        <Detail label={translate("resources.sponsorings.fields.end_date")}>
          <EndDateField record={record} />
        </Detail>
        <Detail label={translate("resources.sponsorings.fields.sales_id")}>
          {record.sales_id != null ? (
            <ReferenceField source="sales_id" reference="sales" link={false}>
              <SaleName />
            </ReferenceField>
          ) : (
            <span className="text-muted-foreground">
              {translate("resources.sponsorings.no_owner")}
            </span>
          )}
        </Detail>
        {record.description ? (
          <Detail
            label={translate("resources.sponsorings.fields.description")}
            className="sm:col-span-2"
          >
            <p className="whitespace-pre-line leading-6">
              {record.description}
            </p>
          </Detail>
        ) : null}
      </dl>

      <CompanyDetails companyId={record.company_id} />

      <div>
        <Separator className="mb-4" />
        <InfiniteListBase
          resource="sponsoring_notes"
          filter={{ sponsoring_id: record.id }}
          sort={{ field: "date", order: "DESC" }}
          perPage={25}
          disableSyncWithLocation
          storeKey={false}
          empty={<NoteCreate reference="sponsorings" />}
        >
          <NotesIterator reference="sponsorings" />
        </InfiniteListBase>
      </div>
    </div>
  );
};

/**
 * The company behind the sponsoring: address, invoice e-mail and its contact
 * persons (the first one is the main contact), so the committee does not
 * have to open the company page for the basics.
 */
const CompanyDetails = ({
  companyId,
}: {
  companyId: Sponsoring["company_id"];
}) => {
  const translate = useTranslate();
  const { data: company, isPending: isPendingCompany } = useGetOne<Company>(
    "companies",
    { id: companyId },
  );
  const { data: contacts = [], isPending: isPendingContacts } =
    useGetList<Contact>("contacts", {
      filter: { company_id: companyId },
      sort: { field: "id", order: "ASC" },
      pagination: { page: 1, perPage: 10 },
    });
  const notFilled = (
    <span className="text-muted-foreground">
      {translate("resources.companies.not_filled")}
    </span>
  );

  return (
    <section className="rounded-lg border border-border bg-muted/40 p-4">
      <h3 className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {translate("resources.companies.name", { smart_count: 1 })}
      </h3>
      {isPendingCompany || isPendingContacts ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <Detail label={translate("resources.companies.fields.address")}>
            {company?.address ? (
              <span className="whitespace-pre-line">{company.address}</span>
            ) : (
              notFilled
            )}
          </Detail>
          <Detail label={translate("resources.companies.fields.invoice_email")}>
            {company?.invoice_email ? (
              <span className="break-all">{company.invoice_email}</span>
            ) : (
              notFilled
            )}
          </Detail>
          <Detail
            label={translate("resources.contacts.name", {
              smart_count: contacts.length || 2,
            })}
            className="sm:col-span-2"
          >
            {contacts.length === 0 ? (
              <span className="text-muted-foreground">
                {translate("resources.companies.no_contacts")}
              </span>
            ) : (
              <ul className="flex flex-col gap-1">
                {contacts.map((contact) => (
                  <li key={contact.id} className="flex flex-wrap gap-x-3">
                    <Link
                      to={`/contacts/${contact.id}/show`}
                      className="font-medium text-brand-navy hover:underline"
                    >
                      {contactFullName(contact)}
                    </Link>
                    {contact.email ? (
                      <a
                        href={`mailto:${contact.email}`}
                        className="text-muted-foreground break-all hover:underline"
                      >
                        {contact.email}
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Detail>
        </dl>
      )}
    </section>
  );
};

const Detail = ({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) => (
  <div className={className}>
    <dt className="text-xs text-muted-foreground tracking-wide">{label}</dt>
    <dd className="text-sm mt-0.5">{children}</dd>
  </div>
);
